import { consolePath } from "../app/brand.js";
import { appendFindingFilters, toFindingFilters } from "../features/findings/model.js";
import type {
  AuditEventPage,
  CheckRunProjection,
  ComparisonResult,
  CodexSecurityRoe,
  CodexSecurityRunResult,
  ComponentRiskProfileState,
  ConsoleSummary,
  CreateEstateGraphFactInput,
  CreateRemediationProposalInput,
  CreateRemediationRoeInput,
  CreateThreatModelInput,
  CreateThreatModelReproductionInput,
  EstateGraphFactRevisionView,
  ExportResult,
  FindingAssigneeOption,
  FindingAssignmentBatchInput,
  FindingAssignmentBatchResult,
  FindingFacets,
  FindingFilters,
  FindingPage,
  FindingPrivateState,
  FindingQueryState,
  FindingView,
  GithubWebhookDelivery,
  HumanValidationInput,
  HumanValidationResponse,
  RegisterRepositoryInput,
  RemediationProposal,
  RemediationProposalStatus,
  RemediationRoe,
  RepositoryBusinessTierState,
  RepositoryComponentRiskItem,
  RepositoryPage,
  RepositoryRecord,
  SavedFindingView,
  ScanPage,
  ScanRecord,
  ScanView,
  TenantMembershipView,
  TenantRole,
  ThreatModelRevisionView,
  ThreatModelWorkspace,
} from "./types.js";

const API_ROOT = "/api/v1";

interface ApiErrorBody {
  code?: string;
  error?: string;
  message?: string;
  requestId?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly requestId: string | null;

  constructor(message: string, status: number, code: string | null, requestId: string | null) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

/**
 * Exported so that `api/model.ts` builds its paths through the same guard
 * rather than a second, subtly different one. The guard is the point: an API
 * path is always same-origin and absolute, and a settings surface that takes a
 * base URL from an operator is exactly the place a relative-path bug would turn
 * into an off-origin request carrying the session cookie.
 */
export function apiPath(path: string, params?: URLSearchParams): string {
  if (!path.startsWith("/") || path.startsWith("//")) {
    throw new Error("API paths must be same-origin absolute paths");
  }
  const query = params?.toString();
  return `${API_ROOT}${path}${query ? `?${query}` : ""}`;
}

function withSignal(signal?: AbortSignal): Pick<RequestInit, "signal"> {
  return signal ? { signal } : {};
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody = {};
  if (response.headers.get("content-type")?.includes("application/json")) {
    try {
      body = (await response.json()) as ApiErrorBody;
    } catch {
      body = {};
    }
  }
  const message = body.message ?? body.error ?? `Request failed with status ${response.status}`;
  if (response.status === 401 && typeof window !== "undefined") {
    const path = window.location.pathname;
    const isConsolePath = path === "/app" || path.startsWith("/app/");
    const isLoginPath = path === "/app/login" || path === "/login";
    if (isConsolePath && !isLoginPath) window.location.replace(consolePath("/login"));
  }
  return new ApiError(message, response.status, body.code ?? null, body.requestId ?? null);
}

function isMutation(method: string | undefined): boolean {
  return ["POST", "PUT", "PATCH", "DELETE"].includes((method ?? "GET").toUpperCase());
}

async function csrfHeader(signal?: AbortSignal): Promise<Record<string, string>> {
  const response = await fetch(apiPath("/auth/session"), {
    credentials: "same-origin",
    headers: { Accept: "application/json" },
    ...withSignal(signal),
  });
  if (!response.ok) throw await parseError(response);
  const session = (await response.json()) as { csrf?: unknown };
  if (typeof session.csrf !== "string" || session.csrf.length < 16) {
    throw new ApiError(
      "The authenticated session did not provide CSRF protection",
      403,
      null,
      null,
    );
  }
  return { "X-Assure-CSRF": session.csrf };
}

/**
 * Exported for `api/model.ts`, so the provider routes inherit the CSRF
 * preflight, the `credentials: "same-origin"` policy and the `ApiError`
 * translation unchanged. Duplicating any of those for the one surface that
 * carries third-party credentials would be the wrong place to fork.
 */
export async function requestJson<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  if (isMutation(init.method) && !headers.has("X-Assure-CSRF")) {
    for (const [name, value] of Object.entries(await csrfHeader(init.signal ?? undefined))) {
      headers.set(name, value);
    }
  }
  const response = await fetch(path, {
    ...init,
    credentials: "same-origin",
    headers,
  });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

function sanitizedFilename(header: string | null, fallback = "assure-findings.csv"): string {
  const match = header?.match(/filename="?([^";]+)"?/i);
  const candidate = match?.[1]?.replaceAll(/[^a-zA-Z0-9._-]/g, "-");
  return candidate && /\.[a-z0-9]+$/iu.test(candidate) ? candidate : fallback;
}

export const api = {
  getThreatModels(snapshotId: string, signal?: AbortSignal): Promise<ThreatModelWorkspace> {
    return requestJson(
      apiPath("/threat-models", new URLSearchParams({ snapshotId })),
      withSignal(signal),
    );
  },

  createThreatModel(
    input: CreateThreatModelInput,
    signal?: AbortSignal,
  ): Promise<ThreatModelRevisionView> {
    return requestJson(apiPath("/threat-models"), {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  createEstateGraphFact(
    input: CreateEstateGraphFactInput,
    signal?: AbortSignal,
  ): Promise<EstateGraphFactRevisionView> {
    return requestJson(apiPath("/threat-model-facts"), {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  createThreatModelReproduction(
    input: CreateThreatModelReproductionInput,
    signal?: AbortSignal,
  ): Promise<unknown> {
    return requestJson(apiPath("/threat-model-reproductions"), {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },
  listTenantMemberships(signal?: AbortSignal): Promise<{ items: TenantMembershipView[] }> {
    return requestJson(apiPath("/admin/memberships"), withSignal(signal));
  },

  createTenantMembership(
    principalId: string,
    role: TenantRole,
    signal?: AbortSignal,
  ): Promise<TenantMembershipView> {
    return requestJson(apiPath("/admin/memberships"), {
      method: "POST",
      body: JSON.stringify({ principalId, role }),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  updateTenantMembership(
    principalId: string,
    role: TenantRole,
    signal?: AbortSignal,
  ): Promise<TenantMembershipView> {
    return requestJson(apiPath(`/admin/memberships/${encodeURIComponent(principalId)}`), {
      method: "PATCH",
      body: JSON.stringify({ role }),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  async deleteTenantMembership(principalId: string, signal?: AbortSignal): Promise<void> {
    await requestJson(apiPath(`/admin/memberships/${encodeURIComponent(principalId)}`), {
      method: "DELETE",
      ...withSignal(signal),
    });
  },
  async getSummary(signal?: AbortSignal): Promise<ConsoleSummary> {
    const [dashboard, scanResponse, repositoryResponse] = await Promise.all([
      requestJson<ConsoleSummary["dashboard"]>(apiPath("/dashboard"), withSignal(signal)),
      requestJson<{ items: ScanRecord[] }>(apiPath("/scans"), withSignal(signal)),
      requestJson<{ items: RepositoryRecord[] }>(apiPath("/repositories"), withSignal(signal)),
    ]);
    const repositoryNames = new Map(repositoryResponse.items.map((item) => [item.id, item.name]));
    const activeScans = scanResponse.items
      .filter(
        (scan) =>
          ["queued", "snapshotting", "detecting", "validating", "indexing"].includes(scan.status) &&
          scan.error === null,
      )
      .map((scan) => ({
        ...scan,
        repositoryName: repositoryNames.get(scan.repositoryId) ?? scan.repositoryId,
        snapshotFingerprint: scan.snapshotId,
      }));
    return { dashboard, activeScans };
  },

  getFindings(state: FindingQueryState, signal?: AbortSignal): Promise<FindingPage> {
    const params = new URLSearchParams();
    appendFindingFilters(params, toFindingFilters(state));
    return requestJson<FindingPage>(apiPath("/findings", params), withSignal(signal));
  },

  getFinding(id: string, signal?: AbortSignal): Promise<FindingView> {
    return requestJson<FindingView>(
      apiPath(`/findings/${encodeURIComponent(id)}`),
      withSignal(signal),
    );
  },

  listSavedFindingViews(signal?: AbortSignal): Promise<{ items: SavedFindingView[] }> {
    return requestJson(apiPath("/finding-views"), withSignal(signal));
  },

  listFindingAssignees(signal?: AbortSignal): Promise<{ items: FindingAssigneeOption[] }> {
    return requestJson(apiPath("/finding-assignees"), withSignal(signal));
  },

  setFindingAssignments(
    input: FindingAssignmentBatchInput,
    signal?: AbortSignal,
  ): Promise<FindingAssignmentBatchResult> {
    return requestJson(apiPath("/finding-assignments"), {
      method: "PUT",
      body: JSON.stringify(input),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  createSavedFindingView(
    name: string,
    search: string,
    signal?: AbortSignal,
  ): Promise<SavedFindingView> {
    return requestJson(apiPath("/finding-views"), {
      method: "POST",
      body: JSON.stringify({ name, search }),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  async deleteSavedFindingView(id: string, signal?: AbortSignal): Promise<void> {
    await requestJson(apiPath(`/finding-views/${encodeURIComponent(id)}`), {
      method: "DELETE",
      ...withSignal(signal),
    });
  },

  getFindingPrivateState(
    findingId: string,
    signal?: AbortSignal,
  ): Promise<{ state: FindingPrivateState | null }> {
    return requestJson(
      apiPath(`/findings/${encodeURIComponent(findingId)}/private-state`),
      withSignal(signal),
    );
  },

  updateFindingPrivateState(
    findingId: string,
    input: { read: boolean; categories: string[] },
    signal?: AbortSignal,
  ): Promise<FindingPrivateState> {
    return requestJson(apiPath(`/findings/${encodeURIComponent(findingId)}/private-state`), {
      method: "PUT",
      body: JSON.stringify(input),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  getRepositoryBusinessTier(
    repositoryId: string,
    signal?: AbortSignal,
  ): Promise<{ state: RepositoryBusinessTierState | null }> {
    return requestJson(
      apiPath(`/repositories/${encodeURIComponent(repositoryId)}/business-tier`),
      withSignal(signal),
    );
  },

  updateRepositoryBusinessTier(
    repositoryId: string,
    input: Omit<
      RepositoryBusinessTierState,
      "tenantId" | "repositoryId" | "updatedBy" | "updatedAt"
    >,
    signal?: AbortSignal,
  ): Promise<RepositoryBusinessTierState> {
    return requestJson(apiPath(`/repositories/${encodeURIComponent(repositoryId)}/business-tier`), {
      method: "PUT",
      body: JSON.stringify(input),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  getRepositoryComponents(
    repositoryId: string,
    signal?: AbortSignal,
  ): Promise<{ items: RepositoryComponentRiskItem[] }> {
    return requestJson(
      apiPath(`/repositories/${encodeURIComponent(repositoryId)}/components`),
      withSignal(signal),
    );
  },

  updateComponentRiskProfile(
    repositoryId: string,
    componentKey: string,
    input: Omit<
      ComponentRiskProfileState,
      "tenantId" | "repositoryId" | "componentKey" | "updatedBy" | "updatedAt"
    >,
    signal?: AbortSignal,
  ): Promise<ComponentRiskProfileState> {
    return requestJson(
      apiPath(
        `/repositories/${encodeURIComponent(repositoryId)}/components/${encodeURIComponent(componentKey)}/risk-profile`,
      ),
      {
        method: "PUT",
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
        ...withSignal(signal),
      },
    );
  },

  registerRepository(
    input: RegisterRepositoryInput,
    signal?: AbortSignal,
  ): Promise<RepositoryRecord> {
    return requestJson<RepositoryRecord>(apiPath("/repositories"), {
      method: "POST",
      body: JSON.stringify(input),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  startScan(
    repositoryId: string,
    idempotencyKey = `web-${crypto.randomUUID()}`,
    signal?: AbortSignal,
    analysisFamilies?: readonly string[],
  ): Promise<ScanRecord> {
    return requestJson<ScanRecord>(apiPath("/scans"), {
      method: "POST",
      body: JSON.stringify({
        repositoryId,
        idempotencyKey,
        ...(analysisFamilies && analysisFamilies.length > 0 ? { analysisFamilies } : {}),
      }),
      headers: { "Content-Type": "application/json" },
      ...withSignal(signal),
    });
  },

  getCodexSecurityRoe(signal?: AbortSignal): Promise<CodexSecurityRoe> {
    return requestJson<CodexSecurityRoe>(
      apiPath("/integrations/codex-security/roe"),
      withSignal(signal),
    );
  },

  runCodexSecurityReview(scanId: string, signal?: AbortSignal): Promise<CodexSecurityRunResult> {
    return requestJson<CodexSecurityRunResult>(
      apiPath(`/scans/${encodeURIComponent(scanId)}/codex-security-run`),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        ...withSignal(signal),
      },
    );
  },

  getScanDetail(scanId: string, signal?: AbortSignal): Promise<import("./types.js").ScanDetail> {
    return requestJson<import("./types.js").ScanDetail>(
      apiPath(`/scans/${encodeURIComponent(scanId)}`),
      withSignal(signal),
    );
  },

  async getFindingFacets(state: FindingQueryState, signal?: AbortSignal): Promise<FindingFacets> {
    const facetParams = new URLSearchParams();
    appendFindingFilters(facetParams, toFindingFilters(state));
    try {
      return await requestJson<FindingFacets>(
        apiPath("/findings/facets", facetParams),
        withSignal(signal),
      );
    } catch (error) {
      if (!(error instanceof ApiError) || error.status !== 404) throw error;
    }
    const params = new URLSearchParams();
    appendFindingFilters(params, {
      ...toFindingFilters(state),
      page: 1,
      pageSize: 500,
    });
    const [findingPage, repositoryResponse] = await Promise.all([
      requestJson<FindingPage>(apiPath("/findings", params), withSignal(signal)),
      requestJson<{ items: RepositoryRecord[] }>(apiPath("/repositories"), withSignal(signal)),
    ]);
    const count = <T extends string | number>(values: T[]): Map<T, number> => {
      const result = new Map<T, number>();
      for (const value of values) result.set(value, (result.get(value) ?? 0) + 1);
      return result;
    };
    const asFacets = <T extends string | number>(values: Map<T, number>) =>
      [...values].map(([value, itemCount]) => ({
        value,
        label: String(value),
        count: itemCount,
      }));
    const repositoryCounts = count(findingPage.items.map((item) => item.repositoryId));
    return {
      severities: asFacets(count(findingPage.items.map((item) => item.severity))),
      validationDepths: asFacets(count(findingPage.items.map((item) => item.validationDepth))),
      repositories: repositoryResponse.items.map((repository) => ({
        value: repository.id,
        label: repository.name,
        count: repositoryCounts.get(repository.id) ?? 0,
      })),
      businessTiers: [],
      components: [],
      engines: [],
      enrichmentStates: [],
      remediationStates: [],
      privateReadStates: [],
      privateCategories: [],
      ageBands: [],
      assignees: [],
      slaStates: [],
      rules: asFacets(count(findingPage.items.map((item) => item.ruleId))),
      cwes: asFacets(count(findingPage.items.map((item) => item.cwe))),
      owaspCategories: asFacets(count(findingPage.items.map((item) => item.owasp))),
      languages: asFacets(count(findingPage.items.map((item) => item.language))),
      analysisFamilies: asFacets(
        count(findingPage.items.map((item) => (item.analysisFamily ?? "sast") as string)),
      ) as FindingFacets["analysisFamilies"],
      statuses: asFacets(count(findingPage.items.map((item) => item.status))),
      replayStatuses: asFacets(count(findingPage.items.map((item) => item.evidence.replayStatus))),
    };
  },

  getScans(
    options: {
      page: number;
      pageSize: number;
      query?: string;
      statuses?: string[];
    },
    signal?: AbortSignal,
  ): Promise<ScanPage> {
    return requestJson<{ items: ScanView[] }>(apiPath("/scans"), withSignal(signal)).then(
      (scanResponse) => {
        const query = options.query?.toLocaleLowerCase().trim();
        const filtered = scanResponse.items.filter((scan) => {
          const repositoryName = scan.repositoryName ?? scan.repositoryId;
          return (
            (!query || `${scan.id} ${repositoryName}`.toLocaleLowerCase().includes(query)) &&
            (!options.statuses?.length || options.statuses.includes(scan.status))
          );
        });
        const start = (options.page - 1) * options.pageSize;
        return {
          items: filtered.slice(start, start + options.pageSize).map((scan) => ({
            ...scan,
            repositoryName: scan.repositoryName ?? scan.repositoryId,
            snapshotFingerprint: scan.snapshotId,
          })),
          total: filtered.length,
          page: options.page,
          pageSize: options.pageSize,
        };
      },
    );
  },

  async getRepositories(query: string, signal?: AbortSignal): Promise<RepositoryPage> {
    const [repositoryResponse, scanResponse] = await Promise.all([
      requestJson<{ items: RepositoryRecord[] }>(apiPath("/repositories"), withSignal(signal)),
      requestJson<{ items: ScanRecord[] }>(apiPath("/scans"), withSignal(signal)),
    ]);
    const normalized = query.trim().toLocaleLowerCase();
    const items = repositoryResponse.items
      .filter((repository) =>
        normalized
          ? `${repository.name} ${repository.authorizationRef}`
              .toLocaleLowerCase()
              .includes(normalized)
          : true,
      )
      .map((repository) => ({
        ...repository,
        latestSnapshot: null,
        latestScan:
          scanResponse.items
            .filter((scan) => scan.repositoryId === repository.id)
            .sort((left, right) => right.startedAt.localeCompare(left.startedAt))[0] ?? null,
      }));
    return { items, total: items.length };
  },

  getAuditEvents(
    options: {
      page: number;
      pageSize: number;
      query?: string;
      entityId?: string;
      entityType?: string;
      action?: string;
    },
    signal?: AbortSignal,
  ): Promise<AuditEventPage> {
    const params = new URLSearchParams({
      offset: String((options.page - 1) * options.pageSize),
      limit: String(options.pageSize),
    });
    if (options.entityId) {
      // Exact-match filter applied server-side (see `/api/v1/audit`).
      params.set("entityId", options.entityId);
    }
    if (options.entityType) params.set("entityType", options.entityType);
    if (options.action) params.set("action", options.action);
    return requestJson<{ items: AuditEventPage["items"] }>(
      apiPath("/audit", params),
      withSignal(signal),
    ).then((response) => {
      const normalized = options.query?.trim().toLocaleLowerCase();
      const rawCount = response.items.length;
      const items = response.items.filter(
        (event) =>
          !normalized ||
          `${event.action} ${event.actorId} ${event.entityId}`
            .toLocaleLowerCase()
            .includes(normalized),
      );
      return {
        items,
        total: rawCount < options.pageSize ? (options.page - 1) * options.pageSize + rawCount : -1,
        page: options.page,
        pageSize: options.pageSize,
      };
    });
  },

  submitHumanValidation(
    findingId: string,
    input: HumanValidationInput,
    signal?: AbortSignal,
  ): Promise<HumanValidationResponse> {
    return requestJson<HumanValidationResponse>(
      apiPath(`/findings/${encodeURIComponent(findingId)}/validations`),
      {
        method: "POST",
        body: JSON.stringify({
          outcome: input.outcome,
          rationale: input.rationale,
        }),
        headers: { "Content-Type": "application/json" },
        ...withSignal(signal),
      },
    );
  },

  setSuppression(
    findingId: string,
    suppressed: boolean,
    rationale: string,
    signal?: AbortSignal,
  ): Promise<FindingView> {
    return requestJson<FindingView>(
      apiPath(`/findings/${encodeURIComponent(findingId)}/suppression`),
      {
        method: suppressed ? "POST" : "DELETE",
        body: JSON.stringify({ rationale }),
        headers: { "Content-Type": "application/json" },
        ...withSignal(signal),
      },
    );
  },

  async exportFindings(filters: FindingFilters, signal?: AbortSignal): Promise<ExportResult> {
    const params = new URLSearchParams();
    appendFindingFilters(params, filters);
    const response = await fetch(apiPath("/findings/export.csv", params), {
      credentials: "same-origin",
      headers: {
        Accept: "text/csv",
      },
      ...withSignal(signal),
    });
    if (!response.ok) throw await parseError(response);
    return {
      blob: await response.blob(),
      filename: sanitizedFilename(response.headers.get("content-disposition")),
    };
  },

  async exportFindingsJsonl(filters: FindingFilters, signal?: AbortSignal): Promise<ExportResult> {
    const params = new URLSearchParams();
    appendFindingFilters(params, filters);
    const response = await fetch(apiPath("/findings/export.jsonl", params), {
      credentials: "same-origin",
      headers: {
        Accept: "application/x-ndjson, application/jsonl, application/json",
      },
      ...withSignal(signal),
    });
    if (!response.ok) throw await parseError(response);
    return {
      blob: await response.blob(),
      filename: sanitizedFilename(
        response.headers.get("content-disposition"),
        "assure-findings.jsonl",
      ),
    };
  },

  getGithubDeliveries(signal?: AbortSignal): Promise<{ items: GithubWebhookDelivery[] }> {
    return requestJson<{ items: GithubWebhookDelivery[] }>(
      apiPath("/github/deliveries"),
      withSignal(signal),
    );
  },

  getCheckRun(scanId: string, headSha: string, signal?: AbortSignal): Promise<CheckRunProjection> {
    const params = new URLSearchParams({ headSha });
    return requestJson<CheckRunProjection>(
      apiPath(`/github/scans/${encodeURIComponent(scanId)}/check-run`, params),
      withSignal(signal),
    );
  },

  getComparison(
    baseScanId: string,
    headScanId: string,
    signal?: AbortSignal,
  ): Promise<ComparisonResult> {
    const params = new URLSearchParams({ baseScanId, headScanId });
    return requestJson<ComparisonResult>(
      apiPath("/github/comparisons", params),
      withSignal(signal),
    );
  },

  createRemediationProposal(
    findingId: string,
    input: CreateRemediationProposalInput,
    signal?: AbortSignal,
  ): Promise<RemediationProposal> {
    return requestJson<RemediationProposal>(
      apiPath(`/findings/${encodeURIComponent(findingId)}/remediation-proposals`),
      {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
        ...withSignal(signal),
      },
    );
  },

  autoGenerateRemediationProposal(
    findingId: string,
    signal?: AbortSignal,
  ): Promise<RemediationProposal> {
    return requestJson<RemediationProposal>(
      apiPath(`/findings/${encodeURIComponent(findingId)}/remediation-proposals/auto`),
      { method: "POST", ...withSignal(signal) },
    );
  },

  listRemediationProposals(
    options: {
      findingId?: string;
      statuses?: RemediationProposalStatus[];
    } = {},
    signal?: AbortSignal,
  ): Promise<{ items: RemediationProposal[] }> {
    const params = new URLSearchParams();
    if (options.findingId) params.set("findingId", options.findingId);
    if (options.statuses?.length) params.set("statuses", options.statuses.join(","));
    return requestJson<{ items: RemediationProposal[] }>(
      apiPath("/remediation-proposals", params),
      withSignal(signal),
    );
  },

  getRemediationProposal(id: string, signal?: AbortSignal): Promise<RemediationProposal> {
    return requestJson<RemediationProposal>(
      apiPath(`/remediation-proposals/${encodeURIComponent(id)}`),
      withSignal(signal),
    );
  },

  transitionRemediationProposal(
    id: string,
    action: "preview" | "approve" | "reject" | "rollback" | "expire",
    rationale?: string,
    signal?: AbortSignal,
  ): Promise<RemediationProposal> {
    return requestJson<RemediationProposal>(
      apiPath(`/remediation-proposals/${encodeURIComponent(id)}/${action}`),
      {
        method: "POST",
        body: JSON.stringify(rationale ? { rationale } : {}),
        headers: { "Content-Type": "application/json" },
        ...withSignal(signal),
      },
    );
  },

  generateRemediationProposal(id: string, signal?: AbortSignal): Promise<RemediationProposal> {
    return requestJson<RemediationProposal>(
      apiPath(`/remediation-proposals/${encodeURIComponent(id)}/generate`),
      {
        method: "POST",
        ...withSignal(signal),
      },
    );
  },

  verifyRemediationProposal(id: string, signal?: AbortSignal): Promise<RemediationProposal> {
    return requestJson<RemediationProposal>(
      apiPath(`/remediation-proposals/${encodeURIComponent(id)}/verify`),
      {
        method: "POST",
        ...withSignal(signal),
      },
    );
  },

  getRemediationRoe(repositoryId: string, signal?: AbortSignal): Promise<RemediationRoe | null> {
    return requestJson<RemediationRoe | null>(
      apiPath(`/repositories/${encodeURIComponent(repositoryId)}/remediation-roe`),
      withSignal(signal),
    );
  },

  createRemediationRoe(
    repositoryId: string,
    input: CreateRemediationRoeInput,
    signal?: AbortSignal,
  ): Promise<RemediationRoe> {
    return requestJson<RemediationRoe>(
      apiPath(`/repositories/${encodeURIComponent(repositoryId)}/remediation-roe`),
      {
        method: "POST",
        body: JSON.stringify(input),
        headers: { "Content-Type": "application/json" },
        ...withSignal(signal),
      },
    );
  },

  revokeRemediationRoe(repositoryId: string, signal?: AbortSignal): Promise<RemediationRoe> {
    return requestJson<RemediationRoe>(
      apiPath(`/repositories/${encodeURIComponent(repositoryId)}/remediation-roe/revoke`),
      {
        method: "POST",
        ...withSignal(signal),
      },
    );
  },
};
